import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { API_URL } from "../../config";
import "./study-companion.scss";
import { SchemaType } from "@google/generative-ai";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface UploadedFile {
  file: File;
  name: string;
  id: string;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  citations?: Array<{ source: string; page: number }>;
}

interface DragPosition {
  x: number;
  y: number;
}

export const StudyCompanion: React.FC = () => {
  const { client, connected, setConfig } = useLiveAPIContext();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [panelPosition, setPanelPosition] = useState<DragPosition>({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<DragPosition>({ x: 0, y: 0 });
  const [highlightText, setHighlightText] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Define tool for fetching PDF context from ChromaDB
  const chromaDBTool = {
    name: "fetch_study_context",
    description:
      "Fetch relevant study material context from ChromaDB based on a query. Returns text excerpts with page numbers and source PDF names.",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        query: {
          type: SchemaType.STRING,
          description: "The search query to find relevant study materials",
        },
        top_k: {
          type: SchemaType.NUMBER,
          description: "Number of top results to return (default: 5)",
        },
      },
      required: ["query"],
    },
  };

  const highlightPDFTool = {
    name: "highlight_pdf_content",
    description:
      "Highlight specific text content in the currently open PDF viewer at the specified page number.",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        source_pdf: {
          type: SchemaType.STRING,
          description: "The name of the PDF file to highlight in",
        },
        page_number: {
          type: SchemaType.NUMBER,
          description: "The page number to navigate to",
        },
        text_to_highlight: {
          type: SchemaType.STRING,
          description: "The specific text excerpt to highlight",
        },
      },
      required: ["source_pdf", "page_number"],
    },
  };

  // Handler functions
  const handleOpenPDF = async (file: File, pageNumber?: number) => {
    try {
      setPdfError(null);
      
      // Revoke old URL if exists
      if (pdfUrl) {
        try {
          URL.revokeObjectURL(pdfUrl);
        } catch (error) {
          console.error("Error revoking old URL:", error);
        }
      }

      // Check if this is a backend file (empty File object with just the name)
      if (file.size === 0 && file.name) {
        console.log("Fetching PDF from backend:", file.name);
        const response = await fetch(
          `${API_URL}/api/rag/download/${encodeURIComponent(file.name)}`
        );
        
        if (response.ok) {
          const blob = await response.blob();
          console.log("Downloaded blob size:", blob.size, "type:", blob.type);
          
          if (blob.size === 0) {
            setPdfError("Downloaded PDF file is empty");
            return;
          }
          
          const pdfBlob = new Blob([blob], { type: "application/pdf" });
          const url = URL.createObjectURL(pdfBlob);
          console.log("Created PDF URL:", url);

          setPdfUrl(url);
          setSelectedPdf(file);
          setCurrentPage(pageNumber || 1);
        } else {
          const errorText = await response.text();
          console.error("Download failed:", response.status, errorText);
          setPdfError(`Failed to download PDF: ${response.status}`);
        }
      } else if (file.size > 0) {
        // Local file with actual content
        console.log("Opening local PDF:", file.name, "size:", file.size);
        const url = URL.createObjectURL(file);
        setPdfUrl(url);
        setSelectedPdf(file);
        setCurrentPage(pageNumber || 1);
      } else {
        console.error("Invalid file: no size and no name");
        setPdfError("Invalid PDF file");
      }
    } catch (error) {
      console.error("Error opening PDF:", error);
      setPdfError(`Error opening PDF: ${error}`);
    }
  };

  // Dragging handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (panelRef.current && !panelMinimized) {
      const rect = panelRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && panelRef.current) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      const maxX = window.innerWidth - panelRef.current.offsetWidth;
      const maxY = window.innerHeight - panelRef.current.offsetHeight;
      
      setPanelPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragOffset]);

  // Navigate to specific page
  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const applyHighlight = useCallback(() => {
    if (!highlightText) return;
    document.querySelectorAll('.pdf-text-highlight').forEach(el => {
      (el as HTMLElement).style.backgroundColor = '';
      el.classList.remove('pdf-text-highlight');
    });
    document.querySelectorAll('.react-pdf__Page__textContent span').forEach(span => {
      const text = span.textContent?.trim() || '';
      if (text.length > 2 && highlightText.toLowerCase().includes(text.toLowerCase())) {
        (span as HTMLElement).style.backgroundColor = 'rgba(255, 220, 0, 0.65)';
        span.classList.add('pdf-text-highlight');
      }
    });
  }, [highlightText]);

  // Load available PDFs from backend on component mount
  useEffect(() => {
    const loadAvailablePDFs = async () => {
      try {
        console.log("Loading available PDFs from backend...");
        const response = await fetch(`${API_URL}/api/rag/files`);
        if (response.ok) {
          const data = await response.json();
          console.log("Backend files:", data);
          
          const backendFiles = data.files.map((file: any) => ({
            file: new File([], file.name, { type: "application/pdf" }),
            name: file.name,
            id: file.name,
          }));
          
          if (backendFiles.length > 0) {
            console.log("Adding backend files:", backendFiles);
            setUploadedFiles((prev) => {
              const existingNames = new Set(prev.map((f) => f.name));
              const newFiles = backendFiles.filter(
                (f: any) => !existingNames.has(f.name)
              );
              return [...prev, ...newFiles];
            });
          } else {
            console.log("No backend files available");
          }
        } else {
          console.log("Backend files request failed:", response.status);
        }
      } catch (error) {
        console.log("Error loading backend PDFs:", error);
      }
    };

    loadAvailablePDFs();
  }, []);

  // Configure for audio input + text response with tools
  useEffect(() => {
    const fileList = uploadedFiles.length
      ? `\n\nCurrently uploaded study materials:\n${uploadedFiles.map(f => `- ${f.name}`).join("\n")}`
      : "\n\nNo study materials uploaded yet.";

    setConfig({
      model: "models/gemini-2.5-flash-native-audio-preview-12-2025",
      generationConfig: {
        responseModalities: "audio",
      },
      systemInstruction: {
        parts: [
          {
            text: "You are a helpful study assistant. For every question:\n1. ALWAYS call fetch_study_context FIRST before saying anything. Wait for the results.\n2. Then answer based ONLY on what the tool returned. Be concise.\n3. After answering, call highlight_pdf_content to show the source page.\nIf the context doesn't contain the answer, say so briefly." + fileList,
          },
        ],
      },
      tools: [
        {
          googleSearch: {},
        },
        {
          functionDeclarations: [chromaDBTool, highlightPDFTool],
        },
      ],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setConfig, uploadedFiles]);

  // Listen for tool calls
  useEffect(() => {
    const handleToolCall = async (toolCall: any) => {
      console.log("Received toolCall event:", toolCall);
      
      const functionCalls = toolCall.functionCalls || [];
      
      for (const call of functionCalls) {
        const toolName = call.name;
        const toolInput = call.args || {};
        const callId = call.id;

        console.log("Processing tool call:", toolName, toolInput);

        if (toolName === "fetch_study_context") {
          try {
            console.log("Fetching study context for query:", toolInput.query);
            const response = await fetch(`${API_URL}/api/rag/query`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                query: toolInput.query,
                top_k: toolInput.top_k || 10,
              }),
            });

            if (response.ok) {
              const data = await response.json();
              console.log("RAG Query results:", data.results);
              const contextResults = data.results.map((result: any) => ({
                text: result.text,
                source: result.source,
                page_num: result.page_num,
                relevance_score: result.relevance_score,
              }));

              client.sendToolResponse({
                functionResponses: [
                  {
                    id: callId,
                    response: {
                      success: true,
                      results: contextResults,
                      total_results: contextResults.length,
                    },
                  },
                ],
              });

              sessionStorage.setItem("fetchedContext", JSON.stringify(data.results));
            }
          } catch (error) {
            console.error("Tool fetch error:", error);
            client.sendToolResponse({
              functionResponses: [
                {
                  id: callId,
                  response: {
                    success: false,
                    error: String(error),
                  },
                },
              ],
            });
          }
        } else if (toolName === "highlight_pdf_content") {
          try {
            console.log("Highlighting PDF:", toolInput.source_pdf, "page:", toolInput.page_number);
            const targetPdf = uploadedFiles.find((f) =>
              f.name.toLowerCase().includes(toolInput.source_pdf.toLowerCase())
            );

            if (targetPdf) {
              console.log("Found PDF file, opening:", targetPdf.name);
              handleOpenPDF(targetPdf.file, toolInput.page_number);
              setHighlightText(toolInput.text_to_highlight || null);

              client.sendToolResponse({
                functionResponses: [
                  {
                    id: callId,
                    response: {
                      success: true,
                      message: `Opened ${toolInput.source_pdf} at page ${toolInput.page_number}`,
                    },
                  },
                ],
              });
            } else {
              client.sendToolResponse({
                functionResponses: [
                  {
                    id: callId,
                    response: {
                      success: false,
                      error: `PDF "${toolInput.source_pdf}" not found in uploaded files`,
                    },
                  },
                ],
              });
            }
          } catch (error) {
            console.error("Highlight tool error:", error);
            client.sendToolResponse({
              functionResponses: [
                {
                  id: callId,
                  response: {
                    success: false,
                    error: String(error),
                  },
                },
              ],
            });
          }
        }
      }
    };

    if (client && connected) {
      client.on("toolcall", handleToolCall);
    }

    return () => {
      client.off("toolcall", handleToolCall);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, connected, uploadedFiles]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      if (file.type === "application/pdf") {
        const newFile: UploadedFile = {
          file,
          name: file.name,
          id: Math.random().toString(36).substr(2, 9),
        };
        setUploadedFiles((prev) => [...prev, newFile]);
      } else {
        alert(`${file.name} is not a PDF`);
      }
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (id: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
    if (uploadedFiles.find((f) => f.id === id)?.file === selectedPdf) {
      setPdfUrl(null);
      setSelectedPdf(null);
    }
  };

  const uploadPDFsToBackend = async () => {
    if (uploadedFiles.length === 0) {
      alert("Please select PDFs to upload");
      return;
    }

    const formData = new FormData();
    uploadedFiles.forEach((uf) => {
      formData.append("files", uf.file);
    });

    try {
      const response = await fetch(`${API_URL}/api/rag/upload`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      alert(`Successfully uploaded ${data.uploaded_count} PDF(s)`);
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload PDFs");
    }
  };

  const handleSendMessage = async () => {
    if (!question.trim() || !connected || isLoading) return;

    const userMessage: Message = {
      role: "user",
      content: question,
    };

    setMessages((prev) => [...prev, userMessage]);
    setQuestion("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/rag/query`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: question,
          top_k: 5,
        }),
      });

      if (!response.ok) {
        throw new Error("Query failed");
      }

      const data = await response.json();
      const ragContext = data.results
        .map(
          (result: any) =>
            `Source: ${result.source}, Page: ${result.page_num}\nContent: ${result.text}`
        )
        .join("\n\n");

      sessionStorage.setItem("ragCitations", JSON.stringify(data.results));

      const systemPrompt = `You are a helpful study assistant. Answer the following question based on the provided study materials. If the materials don't contain relevant information, say so. Be concise but thorough.

Study Materials Context:
${ragContext}

User Question: ${question}`;

      if (client && connected) {
        client.send({
          text: systemPrompt,
        });

        let assistantResponse = "";
        const handleContent = (data: any) => {
          if (data.parts) {
            data.parts.forEach((part: any) => {
              if (part.text) {
                assistantResponse += part.text;
              }
            });
          }
        };

        const handleTurnComplete = () => {
          const citations = JSON.parse(
            sessionStorage.getItem("ragCitations") || "[]"
          );

          const assistantMessage: Message = {
            role: "assistant",
            content: assistantResponse,
            citations: citations.map((c: any) => ({
              source: c.source,
              page: c.page_num,
            })),
          };

          setMessages((prev) => [...prev, assistantMessage]);
          client.off("content", handleContent);
          client.off("turncomplete", handleTurnComplete);
          setIsLoading(false);
        };

        client.on("content", handleContent);
        client.on("turncomplete", handleTurnComplete);
      }
    } catch (error) {
      console.error("Query error:", error);
      alert("Failed to query documents");
      setIsLoading(false);
    }
  };

  const handleCitationClick = (source: string, page: number) => {
    const file = uploadedFiles.find((f) =>
      f.name.toLowerCase().includes(source.toLowerCase())
    );
    if (file) {
      handleOpenPDF(file.file, page);
    }
  };

  const closePDF = () => {
    if (pdfUrl) {
      try {
        URL.revokeObjectURL(pdfUrl);
      } catch (error) {
        console.error("Error revoking object URL:", error);
      }
    }
    setPdfUrl(null);
    setSelectedPdf(null);
    setCurrentPage(1);
    setHighlightText(null);
    setPdfError(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        try {
          URL.revokeObjectURL(pdfUrl);
        } catch (error) {
          console.error("Error revoking object URL on unmount:", error);
        }
      }
    };
  }, [pdfUrl]);

  return (
    <div className="study-companion-container">
      {!pdfUrl ? (
        <div className="upload-view">
          <div className="study-companion-header">
            <h1>📚 Study Companion</h1>
            <p>Upload PDFs and ask questions about your study materials</p>
          </div>

          <div className="upload-section-center">
            <h3>📁 Your PDFs</h3>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf"
              onChange={handleFileUpload}
              className="file-input"
            />

            <div
              className="upload-box"
              onClick={() => fileInputRef.current?.click()}
            >
              <p className="upload-hint">
                Click to select PDFs or drag and drop
              </p>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="file-list">
                <h4>Selected Files ({uploadedFiles.length})</h4>
                <ul>
                  {uploadedFiles.map((uf) => (
                    <li key={uf.id}>
                      <span className="file-name">{uf.name}</span>
                      <div className="file-actions">
                        <button
                          className="view-btn"
                          onClick={() => handleOpenPDF(uf.file)}
                        >
                          📖 Open
                        </button>
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveFile(uf.id)}
                        >
                          ✕
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="connection-buttons">
              <button
                className="connect-btn"
                onClick={uploadPDFsToBackend}
                disabled={uploadedFiles.length === 0}
              >
                Upload PDFs to Study Assistant
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="pdf-viewer-fullscreen">
          <div className="pdf-display">
            <div className="pdf-controls">
              <button className="close-pdf-btn" onClick={closePDF}>
                ← Back to Library
              </button>
              <h3 className="pdf-title">{selectedPdf?.name}</h3>
              <div className="pdf-navigation">
                <input 
                  type="number" 
                  min="1" 
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page > 0) goToPage(page);
                  }}
                  className="page-input"
                  style={{ width: '60px', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
                />
                <span className="page-info">Go to Page</span>
              </div>
            </div>

            <div className="pdf-content">
              {pdfError && (
                <div className="pdf-error">
                  <p>❌ {pdfError}</p>
                  <button onClick={closePDF}>Go Back</button>
                </div>
              )}

              {pdfUrl && !pdfError && (
                <div className="pdf-react-container">
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  >
                    <Page
                      pageNumber={currentPage}
                      renderTextLayer={true}
                      renderAnnotationLayer={false}
                      onRenderTextLayerSuccess={applyHighlight}
                      width={Math.min(window.innerWidth * 0.6, 900)}
                    />
                  </Document>
                  {numPages > 0 && (
                    <p className="pdf-page-count">Page {currentPage} of {numPages}</p>
                  )}
                </div>
              )}

              {highlightText && (
                <div className="highlight-indicator">
                  📍 Highlighting: "{highlightText}"
                  <button 
                    className="clear-highlight-btn"
                    onClick={() => setHighlightText(null)}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          <div
            ref={panelRef}
            className={`floating-panel ${panelMinimized ? "minimized" : ""} ${isDragging ? "dragging" : ""}`}
            style={{
              left: `${panelPosition.x}px`,
              top: `${panelPosition.y}px`,
            }}
          >
            <div 
              className="panel-header" 
              onMouseDown={handleMouseDown}
            >
              <div className="panel-title">
                <span className="drag-handle">⋮⋮</span>
                Study Assistant
              </div>
              <button
                className="minimize-toggle"
                onClick={() => setPanelMinimized(!panelMinimized)}
              >
                {panelMinimized ? "▼" : "▲"}
              </button>
            </div>

            {!panelMinimized && (
              <div className="panel-content">
                <div className="messages-container">
                  {messages.length === 0 && (
                    <div className="welcome-message">
                      <p>👋 Ask me anything about this document!</p>
                    </div>
                  )}

                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`message message-${msg.role}`}
                    >
                      <div className="message-content">
                        <p>{msg.content}</p>
                        {msg.citations && msg.citations.length > 0 && (
                          <div className="citations">
                            <strong>📖 Sources:</strong>
                            <ul>
                              {msg.citations.map((cite, cidx) => (
                                <li key={cidx}>
                                  <button
                                    className="citation-btn"
                                    onClick={() =>
                                      handleCitationClick(cite.source, cite.page)
                                    }
                                  >
                                    {cite.source} - Page {cite.page}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="message message-assistant loading">
                      <div className="message-content">
                        <p>Searching your documents...</p>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="input-section">
                  <textarea
                    className="question-input"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask a question..."
                    disabled={!connected || isLoading}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    rows={2}
                  />
                  <button
                    className="send-btn"
                    onClick={handleSendMessage}
                    disabled={!connected || isLoading || !question.trim()}
                  >
                    Send
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};